const radiatorOverlayPositions = [
  { top: "43%", left: "10%", width: "9.5%", height: "14%", rotate: "-24deg" },
  { top: "30%", left: "20%", width: "10%", height: "14%", rotate: "-24deg" },
  { top: "17%", left: "31.5%", width: "9.5%", height: "13%", rotate: "-24deg"  },
];

const radiatorBubblePositions = [
  { top: "55%", left: "19.5%" },
  { top: "33%", left: "33%" },
  { top: "20%", left: "46%" },
];

const heatwaveParticles = [
  { left: "20%", size: "28%", delay: "0s", duration: "1.1s" },
  { left: "48%", size: "34%", delay: "0.25s", duration: "1.25s" },
  { left: "70%", size: "30%", delay: "0.5s", duration: "1s" },
];
const HEATWAVE_ON_THRESHOLD = 10;

const BLUE = [0, 157, 220];
const ORANGE = [241, 143, 1];
const RED = [255, 55, 40];

const mixColor = (from, to, ratio) => from.map((value, idx) => Math.round(value + (to[idx] - value) * ratio));

const getRadiatorColor = (throttle) => {
  if (throttle <= 10) return BLUE;
  if (throttle <= 50) {
    const ratio = (throttle - 10) / 50;
    return mixColor(ORANGE, RED, ratio);
  }
  return RED;
};

const getRadiatorBubbleConfig = (showComfort, showHot, showCold, throttle) => {
  if (showComfort) {
    if (throttle <= 50) {
      return {
        src: "/positive_feedback.png",
        alt: "Positive feedback",
        width: "110px",
      };
    } else if (throttle > 50) {
      return {
        src: "/hot_bubble.png",
        alt: "Too hot",
        width: "110px",
      };
    }
  } else if (showHot) {
    return {
      src: "/hot_bubble.png",
      alt: "Too hot",
      width: "110px",
    };
  } else if (showCold) {
    return {
      src: "/cold_bubble.svg",
      alt: "Too cold",
      width: "110px",
    };
  }
  return null;
};

const getIndoorBubbleConfig = (showComfort, showHot, showCold) => {
  if (showComfort) {
    return {
      src: "/secondary_positive_feedback.png",
      alt: "Positive feedback",
      top: "39%",
      right: "38%",
      width: "130px",
    };
  } else if (showHot) {
    return {
      src: "/hot_bubble.png",
      alt: "Too hot",
      top: "39%",
      right: "38%",
      width: "130px",
    };
  } else if (showCold) {
    return {
      src: "/cold_bubble.svg",
      alt: "Too cold",
      top: "39%",
      right: "38%",
      width: "130px",
    };
  }
  return null;
};

const ClassroomBackdrop = ({
  isDaytime,
  currentIndoorTemp,
  radiatorThrottlePercents = [0, 0, 0],
}) => {
  const showComfort =
    isDaytime && currentIndoorTemp >= 20 && currentIndoorTemp <= 23;
  const showHot = isDaytime && currentIndoorTemp > 23;
  const showCold = isDaytime && currentIndoorTemp < 20;
  const perRadiatorThrottle = radiatorOverlayPositions.map((_, idx) =>
    Math.max(0, Math.min(100, Number(radiatorThrottlePercents?.[idx] ?? 0))),
  );

  const indoorBubbleConfig = getIndoorBubbleConfig(showComfort, showHot, showCold);
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "65%",
      }}
    >
      <img
        src="/classroom_daytime.png"
        alt="Classroom daytime"
        style={{
          objectFit: "none",
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          opacity: isDaytime ? 1 : 0,
          transition: "opacity 1s ease",
        }}
      />
      <img
        src="/classroom_nighttime.png"
        alt="Classroom nighttime"
        style={{
          objectFit: "none",
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          opacity: isDaytime ? 0 : 1,
          transition: "opacity 1s ease",
        }}
      />
      {radiatorOverlayPositions.map((position, index) => {
        const throttle = perRadiatorThrottle[index];
        const color = getRadiatorColor(throttle);
        const tintColor = `${color[0]}, ${color[1]}, ${color[2]}`;
        const tintOpacity = throttle / 100;
        const heatwaveLevel = Math.max(0, (throttle - HEATWAVE_ON_THRESHOLD) / (100 - HEATWAVE_ON_THRESHOLD),);
        const showHeatwaves = heatwaveLevel > 0;
        const heatwaveOpacity = 0.2 + heatwaveLevel * 0.7;
        const heatwaveScale = 0.45 + heatwaveLevel * 0.9;
        return (
          <div key={`radiator-visual-${index}`}>
            <div
              style={{
                position: "absolute",
                top: position.top,
                left: position.left,
                width: position.width,
                height: position.height,
                transform: `rotate(${position.rotate})`,
                transformOrigin: "center center",
                borderRadius: "24px",
                backgroundColor: `rgba(${tintColor}, ${tintOpacity})`,
                boxShadow: `0 0 70px rgba(${tintColor}, ${Math.min(1, tintOpacity * 0.9)})`,
                mixBlendMode: "multiply",
                transition:
                  "background-color 180ms ease, box-shadow 180ms ease",
              }}
            />
            {showHeatwaves && (
              <div
                style={{
                  position: "absolute",
                  top: position.top,
                  left: position.left,
                  width: position.width,
                  height: position.height,
                  transform: `rotate(${position.rotate})`,
                  transformOrigin: "center center",
                  pointerEvents: "none",
                  zIndex: 2,
                  overflow: "visible",
                }}
              >
                {heatwaveParticles.map((particle, particleIdx) => (
                  <img
                    key={`radiator-heatwave-${index}-${particleIdx}`}
                    src="/heatwaves.svg"
                    alt=""
                    aria-hidden="true"
                    className="radiator-heatwave"
                    style={{
                      position: "absolute",
                      bottom: "68%",
                      left: particle.left,
                      width: particle.size,
                      opacity: heatwaveOpacity,
                      "--heatwave-scale": heatwaveScale,
                      animationDelay: particle.delay,
                      animationDuration: particle.duration,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {isDaytime && indoorBubbleConfig && (
        <>
          <img
            src={indoorBubbleConfig.src}
            alt={indoorBubbleConfig.alt}
            className="pop-up-bubble"
            style={{
              position: "absolute",
              top: indoorBubbleConfig.top,
              right: indoorBubbleConfig.right,
              width: indoorBubbleConfig.width,
              zIndex: 2,
            }}
          />
          {radiatorBubblePositions.map((position, index) => {
            const bubble = getRadiatorBubbleConfig(showComfort, showHot, showCold, perRadiatorThrottle[index]);
            return (
              <img
                key={`radiator-bubble-${index}`}
                src={bubble.src}
                alt={bubble.alt}
                className="pop-up-bubble"
                style={{
                  position: "absolute",
                  top: position.top,
                  left: position.left,
                  width: bubble.width,
                  zIndex: 2,
                }}
              />
            );
          })}
        </>
      )}
    </div>
  );
};

export default ClassroomBackdrop;
